import React from "react";
import { differenceInCalendarDays } from "date-fns";
import ScheduleOutlinedIcon from "@mui/icons-material/ScheduleOutlined";
import ErrorOutlineOutlinedIcon from "@mui/icons-material/ErrorOutlineOutlined";
import DeleteOutlineSharpIcon from "@mui/icons-material/DeleteOutlineSharp";
import type { PersonalAccessToken } from "target_wasm";
import { Badge } from "components/common/Badge";
import { Button } from "components/common/Button";
import { Fold } from "components/common/Fold";
import { Tooltip } from "components/common/Tooltip";
import { TooltipButton } from "components/common/TooltipButton";
import { ContextMenu } from "components/common/ContextMenu";
import { useContextMenu } from "components/hooks/useContextMenu";
import { formatDay, relativeDay } from "./utils";

export const ROW_GRID =
  "grid grid-cols-[minmax(0,2fr)_--spacing(26)_minmax(0,1fr)_minmax(0,1fr)_--spacing(20)] items-center gap-x-3 px-2 max-sm:grid-cols-[minmax(0,1fr)_auto] max-sm:gap-y-1 max-sm:py-2";

// The contextual variant paints the icon `fg` and washes it accent on hover.
const TRASH_REVOKE = "text-error! hover:text-error! hover:bg-error-bg!";
const TRASH_REMOVE = "text-fg-muted! hover:text-fg! hover:bg-bg-hover!";

const capitalize = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

interface TokenRowProps {
  token: PersonalAccessToken;
  confirming: boolean;
  onConfirming: (open: boolean) => void;
  onRevoke: () => void;
  onRemove: () => void;
  highlighted: boolean;
  onHighlightEnd: () => void;
}

export const TokenRow: React.FC<TokenRowProps> = ({
  token,
  confirming,
  onConfirming,
  onRevoke,
  onRemove,
  highlighted,
  onHighlightEnd,
}) => {
  const menu = useContextMenu();
  const name = token.get_description;
  const status = token.get_status;
  const active = status === "active";
  const write = token.get_scope === "write";
  // `get_created_at` is in seconds, `get_expires_at` in milliseconds, and 0
  // means the token never expires.
  const created = new Date(Number(token.get_created_at) * 1000);
  const expiresAt = Number(token.get_expires_at);
  const expires = expiresAt ? new Date(expiresAt) : null;
  const soon =
    active &&
    expires !== null &&
    differenceInCalendarDays(expires, new Date()) <= 7;

  const expiresCell =
    status === "revoked" ? (
      <span className="text-fg-muted">Revoked</span>
    ) : status === "expired" ? (
      <Tooltip title={`Expired ${expires ? formatDay(expires) : ""}`}>
        <span className="inline-flex items-center gap-1 text-error">
          <ErrorOutlineOutlinedIcon className="icon-sm" />
          Expired
        </span>
      </Tooltip>
    ) : !expires ? (
      <span className="text-fg-muted">Never</span>
    ) : (
      <Tooltip title={formatDay(expires)}>
        <span
          className={`inline-flex items-center gap-1 ${
            soon ? "text-warning font-medium" : ""
          }`}
        >
          {soon && <ScheduleOutlinedIcon className="icon-sm" />}
          {relativeDay(expires)}
        </span>
      </Tooltip>
    );

  return (
    <div
      role="rowgroup"
      className={`group rounded-app ${highlighted ? "highlight-flash" : ""}`}
      // Once the revoke prompt has unfolded, a scrolled list brings it into view.
      onTransitionEnd={(e) => {
        if (confirming && e.propertyName === "grid-template-rows")
          e.currentTarget.scrollIntoView?.({ block: "nearest" });
      }}
      onAnimationEnd={(e) => {
        if (e.target === e.currentTarget) onHighlightEnd();
      }}
    >
      <div
        role="row"
        tabIndex={0}
        {...menu.triggerProps}
        className={`${ROW_GRID} min-h-(--control-height-lg) rounded-app outline-none group-hover:bg-bg-hover group-focus-within:bg-bg-hover ${
          menu.isOpen ? "bg-bg-hover" : ""
        }`}
      >
        <div
          role="cell"
          className={`flex items-center gap-2 min-w-0 text-fg-semibold ${
            active ? "" : "opacity-60"
          }`}
        >
          <Tooltip title={capitalize(status)}>
            <span
              className={`w-1.75 h-1.75 rounded-full shrink-0 ${
                active ? "bg-success" : "bg-fg-muted"
              }`}
            />
          </Tooltip>
          <span className="truncate">{name}</span>
        </div>
        <div className="sm:contents max-sm:col-span-2 max-sm:row-start-2 max-sm:flex max-sm:flex-wrap max-sm:items-center max-sm:gap-x-3 max-sm:gap-y-1">
          <div role="cell" className={active ? "" : "opacity-60"}>
            <Badge
              label={write ? "Read & write" : "Read"}
              variant={write ? "warning" : "default"}
              size="xs"
              uppercase={false}
            />
          </div>
          <Tooltip title={formatDay(created)}>
            <div role="cell" className="text-fg-muted truncate tabular-nums">
              {relativeDay(created)}
            </div>
          </Tooltip>
          <div role="cell" className="tabular-nums whitespace-nowrap">
            {expiresCell}
          </div>
        </div>
        <div
          role="cell"
          className="justify-self-end opacity-0 transition-opacity group-hover:opacity-100 group-focus-within:opacity-100 [@media(hover:none)]:opacity-100 max-sm:col-start-2 max-sm:row-start-1"
          style={{ transitionDuration: "var(--duration-micro)" }}
        >
          <TooltipButton
            title={active ? "Revoke" : "Remove"}
            aria-label={
              active ? `Revoke ${name}` : `Remove ${name} from the list`
            }
            variant="contextual"
            size="sm"
            className={active ? TRASH_REVOKE : TRASH_REMOVE}
            onClick={active ? () => onConfirming(true) : onRemove}
          >
            <DeleteOutlineSharpIcon />
          </TooltipButton>
        </div>
      </div>
      <Fold open={confirming}>
        <div
          role="alert"
          className="flex flex-wrap items-center gap-x-3 gap-y-2 rounded-app bg-error-bg px-2 pt-2 pb-3"
        >
          <p className="flex-1 basis-55 m-0">
            Revoke <b className="font-semibold">{name}</b>? Apps using it stop
            working.
          </p>
          <Button
            variant="secondary"
            size="sm"
            autoFocus
            onClick={() => onConfirming(false)}
          >
            Cancel
          </Button>
          <Button variant="destructive" size="sm" onClick={onRevoke}>
            Revoke
          </Button>
        </div>
      </Fold>
      <ContextMenu
        items={[
          active
            ? {
                label: "Revoke",
                onClick: () => onConfirming(true),
                danger: true,
              }
            : { label: "Remove", onClick: onRemove },
        ]}
        position={menu.position}
        onClose={menu.close}
        ariaLabel={`Actions for ${name}`}
      />
    </div>
  );
};
